import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { classes } from "../../helper";
import styles from "./Tooltip.module.css";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

type TooltipProps = {
  content: ReactNode;
  placement?: TooltipPlacement;
  followCursor?: boolean;
  anchorToCursor?: boolean;
  showDelay?: number;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

type Coords = { top: number; left: number };

function anchorFor(rect: DOMRect, placement: TooltipPlacement): Coords {
  switch (placement) {
    case "bottom":
      return { top: rect.bottom, left: rect.left + rect.width / 2 };
    case "left":
      return { top: rect.top + rect.height / 2, left: rect.left };
    case "right":
      return { top: rect.top + rect.height / 2, left: rect.right };
    case "top":
    default:
      return { top: rect.top, left: rect.left + rect.width / 2 };
  }
}

const Tooltip = ({
  content,
  placement = "top",
  followCursor = false,
  anchorToCursor = false,
  showDelay = 0,
  disabled = false,
  className,
  children,
}: TooltipProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const clearTimer = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const showAt = (e?: React.MouseEvent) => {
    if ((followCursor || anchorToCursor) && e) {
      setCoords({ top: e.clientY, left: e.clientX });
      return;
    }
    const el = triggerRef.current;
    if (el) {
      setCoords(anchorFor(el.getBoundingClientRect(), placement));
    }
  };

  const show = (e?: React.MouseEvent) => {
    if (showDelay <= 0) {
      showAt(e);
      return;
    }
    const { clientX, clientY } = e ?? ({} as React.MouseEvent);
    clearTimer();
    timeoutRef.current = setTimeout(() => {
      showAt(e ? ({ clientX, clientY } as React.MouseEvent) : undefined);
    }, showDelay);
  };

  const hide = () => {
    clearTimer();
    setCoords(null);
  };

  useEffect(() => clearTimer, []);

  if (disabled || content === null || content === undefined || content === "") {
    return <>{children}</>;
  }

  return (
    <span
      ref={triggerRef}
      className={classes(styles.wrapper, className ?? "")}
      onMouseEnter={show}
      onMouseMove={
        followCursor || (showDelay > 0 && coords === null) ? show : undefined
      }
      onMouseLeave={hide}
      onFocus={() => show()}
      onBlur={hide}
    >
      {children}
      {coords &&
        createPortal(
          <span
            role="tooltip"
            className={classes(styles.tooltip, styles[placement])}
            style={{ top: coords.top, left: coords.left }}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
